import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const UpdateTemplateBodySchema = z.object({
  id: z.string(),
  groupedType: z.string().nullable().optional(),
  locale: z.string(),
  body: z.string().nullable(),
  subject: z.string().nullable().optional(),
  fromName: z.string().nullable().optional(),
  fromEmail: z.string().nullable().optional(),
  replyToName: z.string().nullable().optional(),
  replyToEmail: z.string().nullable().optional(),
});

const UpdateTemplateVariablesSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  stepType: z.string().optional(),
  includePDF: z.boolean().optional(),
  includeUbl: z.boolean().optional(),
  bodies: z
    .object({
      email: z.array(UpdateTemplateBodySchema),
      letter: z.array(UpdateTemplateBodySchema),
    })
    .optional(),
});

export type UpdateTemplateVariables = z.infer<typeof UpdateTemplateVariablesSchema>;

async function updateTemplateFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateTemplateVariables,
) {
  const { id, ...payload } = UpdateTemplateVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  return instance.put(`/templates/${id}`, payload);
}

export function useUpdateTemplate() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateTemplateVariables>({
    mutationFn: variables => updateTemplateFn(axiosInstance, variables),
    onSuccess: (_data, { id }) => {
      client.invalidateQueries({ queryKey: ['template-v2', id] });
      client.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateTemplateVariables,
    'updateTemplate'
  >('updateTemplate', mutation);
}
